import { useTranslation } from 'react-i18next'
import { RootState } from '@/redux/reducers'
import { useSelector } from 'react-redux'
import { getRoundLottoConfig } from '@/utils/lotto-type-v2'
import { useState, useMemo } from 'react'
import {
  BetTypeOption,
  DigitModeOption,
} from '@/ui-components/components/BetLotto/PanelSelection/PanelSelection.types'
import {
  DigitMode,
  BetTypeEnum,
} from '@/ui-components/components/BetLotto/BetLotto.types'
import { useGetLimitTypeUI } from './useLimitTypeUI'

// Selector for getting round from Redux store
const selectRound = (state: RootState) => state.games.round

/**
 * Hook for managing bet selection panel state and logic
 * @returns Object containing digit modes, bet types, handlers, and limit type functionality
 */
const useSelectPanel = () => {
  const { t } = useTranslation()
  const round = useSelector(selectRound)
  const config = getRoundLottoConfig(round)
  const getLimitTypeUI = useGetLimitTypeUI()

  // State management
  const [digitMode, setDigitMode] = useState<DigitMode>(DigitMode.THREE_DIGIT)
  const [activeBetType, setActiveBetType] = useState<BetTypeEnum[]>([])

  // Get bet configurations from round config
  const { betThree, betTwo, betOne, betSix, betFour } = config

  /**
   * Available digit modes with their visibility status
   * Only shows modes that have available bet types
   */
  const DIGIT_MODES: DigitModeOption[] = useMemo(
    () =>
      [
        {
          label: t('betDetailPanel.betCategory.bet_three'),
          value: DigitMode.THREE_DIGIT,
          show: Object.keys(betThree).length > 0,
        },
        {
          label: t('betDetailPanel.betCategory.bet_two'),
          value: DigitMode.TWO_DIGIT,
          show: Object.keys(betTwo).length > 0,
        },
        {
          label: t('betDetailPanel.betCategory.bet_one'),
          value: DigitMode.RUNNING,
          show: Object.keys(betOne).length > 0,
        },
        {
          label: t('betDetailPanel.betCategory.bet_four'),
          value: DigitMode.FOUR,
          show: Object.keys(betFour).length > 0,
        },
        {
          label: t('betDetailPanel.betCategory.bet_six'),
          value: DigitMode.SIX,
          show: Object.keys(betSix).length > 0,
        },
      ].filter((mode) => mode.show),
    [t, betThree, betTwo, betOne, betFour, betSix],
  )

  /**
   * Available bet type options based on current digit mode
   */
  const betTypeOptions: BetTypeOption[] = useMemo(() => {
    const currentBetConfig = {
      [DigitMode.THREE_DIGIT]: betThree,
      [DigitMode.TWO_DIGIT]: betTwo,
      [DigitMode.RUNNING]: betOne,
      [DigitMode.FOUR]: betFour,
      [DigitMode.SIX]: betSix,
    }[digitMode]

    return Object.entries(currentBetConfig).map(([key, rate]) => ({
      label: t(`betDetailPanel.betLottoType.${key}`),
      rate: rate.toString(),
      value: key as BetTypeEnum,
    }))
  }, [digitMode, betThree, betTwo, betOne, betFour, betSix, t])

  /**
   * Handles digit mode selection
   * Clears active bet types only when mode actually changes
   */
  const handleSelectDigitMode = (mode: DigitMode) => {
    if (mode !== digitMode) {
      setDigitMode(mode)
      setActiveBetType([])
    }
  }

  /**
   * Handles bet type selection
   * Toggles bet type in active selection
   */
  const handleSelectBetType = (type: BetTypeEnum) => {
    setActiveBetType((prev) =>
      prev.includes(type) ? prev.filter((t) => t !== type) : [...prev, type],
    )
  }

  return {
    DIGIT_MODES,
    digitMode,
    handleSelectDigitMode,
    betTypeOptions,
    activeBetType,
    handleSelectBetType,
    getLimitTypeUI,
  }
}

export default useSelectPanel
